import { useState, useEffect, useRef } from 'react';

/** Delay between typewriter steps (ms). Slightly higher while SSE is open so the eye can follow. */
const MS_PER_TICK = 3;
const MS_PER_TICK_STREAMING = 4;

export type TypewriterRevealOptions = {
  /** Only the latest assistant bubble should animate. */
  enabled: boolean;
  /** True while the network stream is still open (may still grow `target`). */
  isStreaming: boolean;
};

/**
 * Reveal text toward `target` with a typewriter. `displayLen` chases the latest content while
 * `isStreaming` is true and finishes any remaining backlog after the stream closes—so the reply
 * is not shown all at once at the end. TTS in PublicAgentChat uses refs fed from SSE, so audio can
 * stay slightly ahead of the visible string.
 */
export function useTypewriterReveal(target: string, opts: TypewriterRevealOptions): string {
  const { enabled, isStreaming } = opts;
  const [displayLen, setDisplayLen] = useState(0);
  const prevTargetRef = useRef(target);

  useEffect(() => {
    if (target.length < prevTargetRef.current.length) {
      setDisplayLen(0);
    }
    prevTargetRef.current = target;
  }, [target]);

  useEffect(() => {
    if (!enabled) {
      setDisplayLen(target.length);
      return;
    }
    if (displayLen >= target.length) return;
    const backlog = target.length - displayLen;
    /** Smaller bursts = smoother motion; larger backlogs still catch up without stalling. */
    const step =
      backlog > 900 ? 48 : backlog > 500 ? 32 : backlog > 250 ? 20 : backlog > 100 ? 12 : backlog > 40 ? 8 : 3;
    const tick = isStreaming ? MS_PER_TICK_STREAMING : MS_PER_TICK;
    const t = window.setTimeout(() => {
      setDisplayLen((d) => Math.min(d + step, target.length));
    }, tick);
    return () => window.clearTimeout(t);
  }, [target, displayLen, enabled, isStreaming]);

  if (!enabled) return target;
  return target.slice(0, displayLen);
}
